import { MultilineSvgText } from "@gettactic/map-builder/src/components/ViewSVG/MultilineSvgText";
import type { TextShape } from "@gettactic/map-builder/src/types";
import React from "react";
import { useOptionalEditorStore } from "../../store/EditorStoreContext";
import { buildWorldShapeTransform } from "../shapeTransform";

export interface TextSvgProps {
  shape: TextShape;
}

export const TextSvg: React.FC<TextSvgProps> = ({ shape }) => {
  const store = useOptionalEditorStore();
  const groupRef = React.useRef<SVGGElement | null>(null);

  // In the editor the block grows to fit its wrapped lines (never shrinks),
  // as the Konva editor did by measuring its text node.
  React.useLayoutEffect(() => {
    const group = groupRef.current;
    if (!store || !group || typeof group.getBBox !== "function") return;
    const measure = () => {
      const text = group.querySelector("text");
      if (!text) return;
      const box = text.getBBox();
      const needed = Math.ceil(box.y + box.height - shape.y);
      if (needed > shape.height + 1) {
        store
          .getState()
          .updateShape(shape.id, { height: needed }, { history: false });
      }
    };
    measure();
    // Lines re-wrap once the font has loaded.
    const fonts = document.fonts;
    if (fonts?.ready) fonts.ready.then(measure).catch(() => {});
  }, [
    store,
    shape.id,
    shape.text,
    shape.width,
    shape.height,
    shape.y,
    shape.fontSize,
    shape.fontFamily,
    shape.isBold,
    shape.isItalic
  ]);

  const fontSize = shape.fontSize || 20;
  const fontFamily = shape.fontFamily || "Quicksand";
  const textAlign = shape.textAlign || "left";
  const textAnchor =
    textAlign === "center" ? "middle" : textAlign === "right" ? "end" : "start";
  return (
    <g ref={groupRef} transform={buildWorldShapeTransform(shape)}>
      {/* Glyph outlines alone are a poor click target; the whole text box
          is clickable, as in the Konva editor. */}
      <rect
        x={shape.x}
        y={shape.y}
        width={shape.width}
        height={shape.height}
        fill="transparent"
        stroke="none"
      />
      <MultilineSvgText
        x={shape.x}
        y={shape.y}
        width={shape.width}
        height={shape.height}
        fontFamily={fontFamily}
        textAnchor={textAnchor}
        style={{
          fontSize: `${fontSize}px`,
          fontFamily,
          // Konva draws "normal" at weight 400; measuring with any other
          // weight wraps lines differently than the editor.
          fontWeight: shape.isBold ? "700" : "400",
          fontStyle: shape.isItalic ? "italic" : "normal",
          textDecoration: shape.isUnderline ? "underline" : "none",
          fill: shape.textColor || "#B5B5B5"
        }}
      >
        {shape.text}
      </MultilineSvgText>
    </g>
  );
};
